import React from 'react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'react-redux'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { Button } from '@mobi/component-library/Base/Buttons'
import { layering, measurements, spacing } from '@mobi/component-library/Foundations'
import { Toggle } from '@mobi/component-library/Selection&Input/Toggle'
import { hexColors } from '@mobi/settings/hexColors'
import {
  ButtonGroupStyled,
  ButtonGroupItemStyled,
} from '@core/Components/Buttons/ButtonGroup.styles'
import { shouldApplySafeAreaBottomPadding } from '@core/Settings/cssConditions'
import { StructureSearchGroupHeader } from '@core/Areas/StructuredSearch/Components/Common.styles'
import { RangeBoundaries } from '@core/Areas/StructuredSearch/constants'
import type { RaceCodesLowerCase } from '@core/Areas/Racing/Types/MeetingInformation'
import type {
  StructuredSearchFields,
  StructuredSearchTrack,
} from '@core/Areas/StructuredSearch/types'
import { selectSearchCriteria } from '@core/Areas/StructuredSearch/Store/selectors'
import { initialState, setSearchCriteria, setSearchField } from '../../Store'
import { RangeInput } from './Components/RangeInput'
import { LocationCriteria } from './Components/LocationCriteria'
import { CodeSelectionFilter } from './Components/CodeSelector'

export const StructuredSearchCriteria: React.FC<{
  setResultsTab(): void
  totalResultCount: number
  availableTracks?: StructuredSearchTrack[]
}> = ({ setResultsTab, totalResultCount, availableTracks }) => {
  const dispatch = useDispatch()
  const sc = useSelector(selectSearchCriteria) ?? initialState.searchCriteria

  const onChangeCallback = (
    field: keyof StructuredSearchFields,
    value: StructuredSearchFields[typeof field]
  ) => {
    dispatch(setSearchField({ field, value }))
  }

  const resetCallback = () => {
    dispatch(setSearchCriteria(initialState.searchCriteria))
  }

  const contestTypeChangedCallback = ({
    field,
    value,
  }: {
    field: RaceCodesLowerCase
    value: boolean
  }) => {
    let driverField: keyof StructuredSearchFields | null = null
    switch (field) {
      case 'races':
        driverField = 'contestTypeRace'
        break
      case 'trots':
        driverField = 'contestTypeTrot'
        break
      case 'dogs':
        driverField = 'contestTypeDogs'
        break
    }
    if (driverField) {
      dispatch(setSearchField({ field: driverField, value }))
    }
  }

  return (
    <WrapperStyled>
      <div>
        <StructureSearchGroupHeader>Racing Code</StructureSearchGroupHeader>
        <CodeSelectionFilter
          racesSelected={sc.contestTypeRace}
          trotsSelected={sc.contestTypeTrot}
          dogsSelected={sc.contestTypeDogs}
          selectionChangedCallback={contestTypeChangedCallback}
          testId='structured-search'
        />
      </div>

      <div>
        <StructureSearchGroupHeader>Date</StructureSearchGroupHeader>
        <ButtonGroupStyled>
          <ButtonGroupItemStyled
            id='date-today'
            data-tid-date-today=''
            selected={sc.dateToday}
            onClick={() => onChangeCallback('dateToday', !sc.dateToday)}
          >
            Today
          </ButtonGroupItemStyled>
          <ButtonGroupItemStyled
            id='date-tomorrow'
            data-tid-date-tomorrow=''
            selected={sc.dateTomorrow}
            onClick={() => onChangeCallback('dateTomorrow', !sc.dateTomorrow)}
          >
            Tomorrow
          </ButtonGroupItemStyled>
          <ButtonGroupItemStyled
            id='date-beyond-tomorrow'
            data-tid-date-beyond-tomorrow=''
            selected={sc.dateBeyondTomorrow}
            onClick={() => onChangeCallback('dateBeyondTomorrow', !sc.dateBeyondTomorrow)}
          >
            Later
          </ButtonGroupItemStyled>
        </ButtonGroupStyled>
      </div>

      <LocationCriteria availableTracks={availableTracks} />

      <div>
        <StructureSearchGroupHeader>Last Start</StructureSearchGroupHeader>

        <Flex flexDirection='column' gap={spacing.smx1}>
          <Flex justifyContent='space-between' alignItems='center' gap={spacing.smx1}>
            <ToggleLabelStyled htmlFor='won-last-start'>Won Last Start</ToggleLabelStyled>
            <Toggle
              size='md'
              id='won-last-start'
              isOn={sc.wonLastStart}
              testId='Criteria.WonLastStart'
              onToggle={() => onChangeCallback('wonLastStart', !sc.wonLastStart)}
            />
          </Flex>

          <Flex justifyContent='space-between' alignItems='center' gap={spacing.smx1}>
            <ToggleLabelStyled htmlFor='placed-last-start'>Placed Last Start</ToggleLabelStyled>
            <Toggle
              size='md'
              id='placed-last-start'
              testId='Criteria.PlacedLastStart'
              isOn={sc.placedLastStart}
              onToggle={() => onChangeCallback('placedLastStart', !sc.placedLastStart)}
            />
          </Flex>

          <Flex justifyContent='space-between' alignItems='center' gap={spacing.smx1}>
            <ToggleLabelStyled htmlFor='fav-last-start'>Favourite Last Start</ToggleLabelStyled>
            <Toggle
              size='md'
              id='fav-last-start'
              testId='Criteria.FavouriteLastStart'
              isOn={sc.favouriteLastStart}
              onToggle={() => onChangeCallback('favouriteLastStart', !sc.favouriteLastStart)}
            />
          </Flex>

          <div>
            <CriteriaSubtitleLabelStyled>Days Since Last Start</CriteriaSubtitleLabelStyled>
            <RangeInput
              testId='days-since-last-start'
              minRange={RangeBoundaries.minDaysSinceLastStart}
              maxRange={RangeBoundaries.maxDaysSinceLastStart}
              selectedValues={[sc.minDaysSinceLastStart, sc.maxDaysSinceLastStart]}
              minChangedCallback={(value: number) => {
                onChangeCallback('minDaysSinceLastStart', value)
              }}
              maxChangedCallback={(value: number) => {
                onChangeCallback('maxDaysSinceLastStart', value)
              }}
              isMaxInfinite
            />
          </div>

          <div>
            <CriteriaSubtitleLabelStyled>Days Since Last Win</CriteriaSubtitleLabelStyled>
            <RangeInput
              testId='days-since-last-win'
              minRange={RangeBoundaries.minDaysSinceLastWin}
              maxRange={RangeBoundaries.maxDaysSinceLastWin}
              selectedValues={[sc.minDaysSinceLastWin, sc.maxDaysSinceLastWin]}
              minChangedCallback={(value: number) => {
                onChangeCallback('minDaysSinceLastWin', value)
              }}
              maxChangedCallback={(value: number) => {
                onChangeCallback('maxDaysSinceLastWin', value)
              }}
              isMaxInfinite
            />

            <CriteriaSubtitleLabelStyled>Starts Since Last Spell</CriteriaSubtitleLabelStyled>
            <RangeInput
              testId='starts-since-last-spell'
              minRange={RangeBoundaries.minStartsSinceLastSpell}
              maxRange={RangeBoundaries.maxStartsSinceLastSpell}
              selectedValues={[sc.minStartsSinceLastSpell, sc.maxStartsSinceLastSpell]}
              minChangedCallback={(value: number) => {
                onChangeCallback('minStartsSinceLastSpell', value)
              }}
              maxChangedCallback={(value: number) => {
                onChangeCallback('maxStartsSinceLastSpell', value)
              }}
              isMaxInfinite
            />
          </div>
        </Flex>
      </div>

      <div>
        <StructureSearchGroupHeader>This Start</StructureSearchGroupHeader>

        <Flex flexDirection='column' gap={spacing.smx1}>
          <div>
            <CriteriaSubtitleLabelStyled>Fixed Odds Price</CriteriaSubtitleLabelStyled>
            <RangeInput
              testId='fixed-odds-price-range'
              isCurrency={true}
              minRange={RangeBoundaries.minFixedOddsPrice}
              maxRange={RangeBoundaries.maxFixedOddsPrice}
              selectedValues={[sc.minFixedOddsPrice, sc.maxFixedOddsPrice]}
              minChangedCallback={(value: number) => {
                onChangeCallback('minFixedOddsPrice', value)
              }}
              maxChangedCallback={(value: number) => {
                onChangeCallback('maxFixedOddsPrice', value)
              }}
              isMaxInfinite={true}
            />
          </div>

          <Flex justifyContent='space-between' alignItems='center' gap={spacing.smx1}>
            <ToggleLabelStyled htmlFor='first-up'>
              First Up <span>First start since a spell</span>
            </ToggleLabelStyled>

            <Toggle
              size='md'
              id='first-up'
              testId='Criteria.FirstUp'
              isOn={sc.firstUp}
              onToggle={() => onChangeCallback('firstUp', !sc.firstUp)}
            />
          </Flex>

          <Flex justifyContent='space-between' alignItems='center' gap={spacing.smx1}>
            <ToggleLabelStyled htmlFor='second-up'>
              Second Up <span>Second start since a spell</span>
            </ToggleLabelStyled>

            <Toggle
              size='md'
              id='second-up'
              testId='Criteria.SecondUp'
              isOn={sc.secondUp}
              onToggle={() => onChangeCallback('secondUp', !sc.secondUp)}
            />
          </Flex>

          <Flex justifyContent='space-between' alignItems='center' gap={spacing.smx1}>
            <ToggleLabelStyled htmlFor='gear-change'>
              Gear Change <span>Gear changed since last start</span>
            </ToggleLabelStyled>

            <Toggle
              size='md'
              id='gear-change'
              testId='Criteria.GearChange'
              isOn={sc.gearChange}
              onToggle={() => onChangeCallback('gearChange', !sc.gearChange)}
            />
          </Flex>

          <Flex justifyContent='space-between' alignItems='center' gap={spacing.smx1}>
            <ToggleLabelStyled htmlFor='apprentice'>
              Apprentice <span>Apprentice riding</span>
            </ToggleLabelStyled>

            <Toggle
              size='md'
              id='apprentice'
              testId='Criteria.Apprentice'
              isOn={sc.apprentice}
              onToggle={() => onChangeCallback('apprentice', !sc.apprentice)}
            />
          </Flex>

          <div>
            <CriteriaSubtitleLabelStyled>Position (Races)</CriteriaSubtitleLabelStyled>
            <ButtonGroupStyled>
              <ButtonGroupItemStyled
                id='barrier-number-one-to-five'
                data-tid-barrier-number-one-to-five=''
                selected={sc.barrierNumberOneToFive}
                onClick={() =>
                  onChangeCallback('barrierNumberOneToFive', !sc.barrierNumberOneToFive)
                }
              >
                Barrier 1-5
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='barrier-number-six-plus'
                data-tid-barrier-number-six-plus=''
                selected={sc.barrierNumberSixPlus}
                onClick={() => onChangeCallback('barrierNumberSixPlus', !sc.barrierNumberSixPlus)}
              >
                Barrier 6+
              </ButtonGroupItemStyled>
            </ButtonGroupStyled>
          </div>

          <div>
            <CriteriaSubtitleLabelStyled>Position (Dogs)</CriteriaSubtitleLabelStyled>
            <ButtonGroupStyled>
              <ButtonGroupItemStyled
                id='box-number-one-to-four'
                data-tid-box-number-one-to-four=''
                selected={sc.boxNumberOneToFour}
                onClick={() => onChangeCallback('boxNumberOneToFour', !sc.boxNumberOneToFour)}
              >
                Box 1-4
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='box-number-five-plus'
                data-tid-box-number-five-plus=''
                selected={sc.boxNumberFivePlus}
                onClick={() => onChangeCallback('boxNumberFivePlus', !sc.boxNumberFivePlus)}
              >
                Box 5-8
              </ButtonGroupItemStyled>
            </ButtonGroupStyled>
          </div>

          <div>
            <CriteriaSubtitleLabelStyled>Weight Change</CriteriaSubtitleLabelStyled>
            <ButtonGroupStyled>
              <ButtonGroupItemStyled
                id='weight-changed-up'
                data-tid-weight-changed-up=''
                selected={sc.weightChangedUp}
                onClick={() => onChangeCallback('weightChangedUp', !sc.weightChangedUp)}
              >
                Up
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='weight-changed-down'
                data-tid-weight-changed-down=''
                selected={sc.weightChangedDown}
                onClick={() => onChangeCallback('weightChangedDown', !sc.weightChangedDown)}
              >
                Down
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='weight-not-changed'
                data-tid-weight-not-changed=''
                selected={sc.weightNotChanged}
                onClick={() => onChangeCallback('weightNotChanged', !sc.weightNotChanged)}
              >
                Same
              </ButtonGroupItemStyled>
            </ButtonGroupStyled>
          </div>

          <div>
            <CriteriaSubtitleLabelStyled>Distance Change</CriteriaSubtitleLabelStyled>
            <ButtonGroupStyled>
              <ButtonGroupItemStyled
                id='distance-changed-up'
                data-tid-distance-changed-up=''
                selected={sc.distanceChangedUp}
                onClick={() => onChangeCallback('distanceChangedUp', !sc.distanceChangedUp)}
              >
                Up
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='distance-changed-down'
                data-tid-distance-changed-down=''
                selected={sc.distanceChangedDown}
                onClick={() => onChangeCallback('distanceChangedDown', !sc.distanceChangedDown)}
              >
                Down
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='distance-not-changed'
                data-tid-distance-not-changed=''
                selected={sc.distanceNotChanged}
                onClick={() => onChangeCallback('distanceNotChanged', !sc.distanceNotChanged)}
              >
                Same
              </ButtonGroupItemStyled>
            </ButtonGroupStyled>
          </div>
        </Flex>
      </div>

      <div>
        <StructureSearchGroupHeader>Lifetime</StructureSearchGroupHeader>

        <Flex flexDirection='column' gap={spacing.smx1}>
          <div>
            <CriteriaSubtitleLabelStyled>Conditions</CriteriaSubtitleLabelStyled>
            <ButtonGroupStyled>
              <ButtonGroupItemStyled
                id='win-track-condition-good'
                data-tid-win-track-condition-good=''
                selected={sc.winTrackConditionGood}
                onClick={() => onChangeCallback('winTrackConditionGood', !sc.winTrackConditionGood)}
              >
                Good
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='win-track-condition-firm'
                data-tid-win-track-condition-firm=''
                selected={sc.winTrackConditionFirm}
                onClick={() => onChangeCallback('winTrackConditionFirm', !sc.winTrackConditionFirm)}
              >
                Firm
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='win-track-condition-soft'
                data-tid-win-track-condition-soft=''
                selected={sc.winTrackConditionSoft}
                onClick={() => onChangeCallback('winTrackConditionSoft', !sc.winTrackConditionSoft)}
              >
                Soft
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='win-track-condition-heavy'
                data-tid-win-track-condition-heavy=''
                selected={sc.winTrackConditionHeavy}
                onClick={() =>
                  onChangeCallback('winTrackConditionHeavy', !sc.winTrackConditionHeavy)
                }
              >
                Heavy
              </ButtonGroupItemStyled>

              <ButtonGroupItemStyled
                id='win-track-condition-synthetic'
                data-tid-win-track-condition-synthetic=''
                selected={sc.winTrackConditionSynthetic}
                onClick={() =>
                  onChangeCallback('winTrackConditionSynthetic', !sc.winTrackConditionSynthetic)
                }
              >
                Synthetic
              </ButtonGroupItemStyled>
            </ButtonGroupStyled>
          </div>

          <div>
            <CriteriaSubtitleLabelStyled>Career Win Percentage</CriteriaSubtitleLabelStyled>
            <RangeInput
              testId='career-win-percentage'
              minRange={RangeBoundaries.minPercentage}
              maxRange={RangeBoundaries.maxPercentage}
              selectedValues={[sc.careerWinPercentage]}
              minChangedCallback={(value: number) => {
                onChangeCallback('careerWinPercentage', value)
              }}
              isMaxInfinite={false}
              isPercentage
            />
          </div>

          <div>
            <CriteriaSubtitleLabelStyled>Track Win Percentage</CriteriaSubtitleLabelStyled>
            <RangeInput
              testId='track-win-percentage'
              minRange={RangeBoundaries.minPercentage}
              maxRange={RangeBoundaries.maxPercentage}
              selectedValues={[sc.trackWinPercentage]}
              minChangedCallback={(value: number) => {
                onChangeCallback('trackWinPercentage', value)
              }}
              isMaxInfinite
              isPercentage
            />
          </div>

          <div>
            <CriteriaSubtitleLabelStyled>Distance Win Percentage</CriteriaSubtitleLabelStyled>
            <RangeInput
              testId='distance-win-percentage'
              minRange={RangeBoundaries.minPercentage}
              maxRange={RangeBoundaries.maxPercentage}
              selectedValues={[sc.distanceWinPercentage]}
              minChangedCallback={(value: number) => {
                onChangeCallback('distanceWinPercentage', value)
              }}
              isMaxInfinite
              isPercentage
            />
          </div>

          <div>
            <CriteriaSubtitleLabelStyled>Track/Distance Win Percentage</CriteriaSubtitleLabelStyled>
            <RangeInput
              testId='track-distance-win-percentage'
              minRange={RangeBoundaries.minPercentage}
              maxRange={RangeBoundaries.maxPercentage}
              selectedValues={[sc.trackdistanceWinPercentage]}
              minChangedCallback={(value: number) => {
                onChangeCallback('trackdistanceWinPercentage', value)
              }}
              isMaxInfinite
              isPercentage
            />
          </div>
        </Flex>
      </div>

      <CriteriaFixedFooterStyled>
        <div>
          <Button
            isFullWidth
            color='secondary_grey'
            onClick={resetCallback}
            data-testid='structured-search-clear-button'
          >
            Clear
          </Button>

          <Button
            isFullWidth
            color='primary'
            onClick={setResultsTab}
            data-testid='structured-search-submit-button'
          >
            Results ({totalResultCount})
          </Button>
        </div>
      </CriteriaFixedFooterStyled>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
  padding: `${spacing.smx1} ${spacing.sm}`,
  paddingBottom: `calc(${measurements.mobi.bottomTabsHeight} + ${spacing.sm})`,
})

const CriteriaSubtitleLabelStyled = styled.span({
  display: 'block',
  padding: '0.75rem 0',
  fontSize: '1.4rem',
})

const ToggleLabelStyled = styled.label({
  fontSize: '1.4rem',

  '> span': {
    display: 'block',
    fontSize: '1.2rem',
    color: hexColors.grey,
  },
})

const CriteriaFixedFooterStyled = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  zIndex: layering.overlayLow - 1,
  position: 'fixed',
  bottom: `calc(${measurements.mobi.bottomTabsHeight} + ${shouldApplySafeAreaBottomPadding ? 'var(--safe-area-inset-bottom, 0)' : 0})`,
  left: 0,
  right: 0,
  minHeight: measurements.mobi.bottomTabsHeight,
  width: '100%',
  minWidth: measurements.mobi.minWidth,
  backgroundColor: 'rgba(256, 256, 256, 0.95)',

  '> div': {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '40rem',
    gap: spacing.smx1,
    padding: spacing.smx1,
  },
})
